import { Button } from '@/components/ui/button'
import { useProviderRepositories } from '@/hooks'
import { connectionProblemMessage, isUnusableConnection } from '@/lib/connectionErrors'
import type { ConnectorType } from '@/types'
import { ExternalLink, Lock, Unlock } from 'lucide-react'

interface RepositoryPickerProps {
  projectId: string
  connectorType: ConnectorType
  connectorId?: string
  connectionName?: string
  onReconnect?: () => void
}

export function RepositoryPicker({ projectId, connectorType, connectorId, connectionName, onReconnect }: RepositoryPickerProps) {
  const { data: repositories = [], isLoading, isError, error } = useProviderRepositories(projectId, connectorType, true, '', connectorId)

  if (isLoading) {
    return <div role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading repositories...</div>
  }

  if (isError) {
    if (isUnusableConnection(error)) {
      return (
        <div role="alert" className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{connectionProblemMessage(error, connectionName) ?? 'Your token has expired or been revoked.'}</span>
          {onReconnect && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onReconnect}
              aria-label={`Reconnect ${connectionName ?? 'connection'} repositories`}
              data-testid={`repository-picker-reconnect-${connectorId ?? 'connection'}`}
            >
              Reconnect
            </Button>
          )}
        </div>
      )
    }

    return <div role="alert" className="text-sm text-danger-foreground">Failed to load repositories.</div>
  }

  if (repositories.length === 0) {
    return <div className="text-sm text-muted-foreground">No repositories found.</div>
  }

  return (
    <div className="max-h-60 overflow-y-auto rounded-md border">
      {repositories.map((repo) => (
        <div
          key={repo.id}
          className="flex items-center justify-between border-b px-3 py-2 last:border-b-0 hover:bg-muted/50"
        >
          <div className="flex items-center gap-2 min-w-0">
            {repo.isPrivate ? (
              <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            ) : (
              <Unlock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            )}
            <span className="text-sm truncate">{repo.fullName}</span>
          </div>
          {repo.url && (
            <a
              href={repo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground shrink-0 ml-2"
              aria-label={`Open ${repo.fullName} in new window`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      ))}
    </div>
  )
}
